import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { Venue } from "../../api/types";
import { Card, EditorPage, EmptyState, ErrorPanel, Field, FormActions } from "../../ui/components";
import { EditorPageSkeleton } from "../../ui/skeletons";
import { useVenues } from "./queries";

export function VenueEditorPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const venues = useVenues();
  const venue = venueId ? venues.data?.items.find((item) => item.id === venueId) : undefined;
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const backTo = venueId ? `/venues?venue=${encodeURIComponent(venueId)}` : "/venues";

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const payload = {
      name: String(data.get("name")),
      code: String(data.get("code")),
      description: String(data.get("description")),
      timezone: String(data.get("timezone")),
      address: {
        line1: String(data.get("address")),
        city: String(data.get("city")),
        region: String(data.get("region")),
        postalCode: String(data.get("postalCode")),
      },
      contact: { email: String(data.get("email")), phone: String(data.get("phone")) },
      status: String(data.get("status")),
    };
    setWorking(true);
    setError(undefined);
    try {
      const item = await api<Venue>(venue ? `/api/v1/venues/${venue.id}` : "/api/v1/venues", {
        method: venue ? "PUT" : "POST",
        body: jsonBody(payload),
      });
      await queryClient.invalidateQueries({ queryKey: ["venues"] });
      navigate(`/venues?venue=${encodeURIComponent(item.id)}`);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (venueId && venues.isLoading) return <EditorPageSkeleton />;
  if (venues.error) return <ErrorPanel error={venues.error} />;

  return (
    <EditorPage
      title={venue ? "Edit venue" : "Add venue"}
      description={venue?.name ?? "Create a property and its operating details."}
      backTo={backTo}
      backLabel="Back to venues"
    >
      {venueId && !venue ? (
        <EmptyState
          title="Venue not found"
          description="The venue is outside your current access scope or no longer exists."
        />
      ) : (
        <Card>
          {error ? <ErrorPanel error={error} /> : null}
          <form className="form-grid" onSubmit={save} key={venue?.id ?? "new-venue"}>
            <Field label="Name">
              <input name="name" required defaultValue={venue?.name} />
            </Field>
            <Field label="Code">
              <input name="code" required defaultValue={venue?.code} />
            </Field>
            <Field label="Timezone" hint="Use an IANA timezone such as America/New_York.">
              <input
                name="timezone"
                required
                defaultValue={venue?.timezone ?? "America/New_York"}
              />
            </Field>
            <Field label="Status">
              <select name="status" defaultValue={venue?.status ?? "active"}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
            <Field label="Description">
              <textarea name="description" defaultValue={venue?.description} />
            </Field>
            <Field label="Address line">
              <textarea name="address" defaultValue={String(venue?.address?.line1 ?? "")} />
            </Field>
            <Field label="City">
              <input name="city" defaultValue={String(venue?.address?.city ?? "")} />
            </Field>
            <Field label="State or region">
              <input name="region" defaultValue={String(venue?.address?.region ?? "")} />
            </Field>
            <Field label="Postal code">
              <input name="postalCode" defaultValue={String(venue?.address?.postalCode ?? "")} />
            </Field>
            <Field label="Contact email">
              <input name="email" type="email" defaultValue={String(venue?.contact?.email ?? "")} />
            </Field>
            <Field label="Contact phone">
              <input name="phone" defaultValue={String(venue?.contact?.phone ?? "")} />
            </Field>
            <div className="full">
              <FormActions
                submitting={working}
                submitLabel={venue ? "Save venue" : "Add venue"}
                onCancel={() => navigate(backTo)}
              />
            </div>
          </form>
        </Card>
      )}
    </EditorPage>
  );
}
